import { Dialog, PlainButton, ScreenTransitionWrapper } from "@jugl-web/ui-kit";
import {
  ScreenTransitionManagerProvider,
  useScreenTransitionManager,
} from "@jugl-web/utils/utils/ScreenTransitionManager";
import React, { useEffect, useMemo, useState, ReactElement } from "react";
import { useTranslations } from "@jugl-web/utils";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as ShieldIcon } from "./assets/shield.svg";
import { CreatePinScreen } from "./screens/CreatePinScreen";
import { ConfirmPinScreen } from "./screens/ConfirmPinScreen";
import { WhatsappPinDialogScreenToParametersMap } from "./types";
import { ConfirmActionScreen } from "./screens/ConfirmActionScreen/ConfirmActionScreen";

interface WhatsappPinDialogProps {
  isOpen: boolean;
  initialScreen: keyof WhatsappPinDialogScreenToParametersMap;
  isPinInvalid?: boolean;
  verifyActionCallback?: (pin: string) => Promise<void>;
  onRequestClose: () => void;
}

const InnerWhatsappPinDialog: React.FC<WhatsappPinDialogProps> = ({
  isOpen,
  initialScreen,
  isPinInvalid,
  verifyActionCallback,
  onRequestClose,
}) => {
  const { t } = useTranslations();
  const { screen, transitionTo, renderContent } = useScreenTransitionManager();
  const [pin, setPin] = useState("");

  const content = useMemo<ReactElement>(
    () =>
      renderContent({
        createPin: <CreatePinScreen pin={pin} onPinChange={setPin} />,
        confirmPin: <ConfirmPinScreen pin={pin} onClose={onRequestClose} />,
        confirmAction: (
          <ConfirmActionScreen
            verifyActionCallback={verifyActionCallback}
            isPinInvalid={isPinInvalid}
          />
        ),
      }),
    [isPinInvalid, onRequestClose, pin, renderContent, verifyActionCallback]
  );

  useEffect(() => {
    if (isOpen) {
      transitionTo({ name: initialScreen }, { force: true });
      setPin("");
    }
  }, [initialScreen, isOpen, transitionTo]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onRequestClose}
      className="w-full max-w-[680px] rounded-2xl"
    >
      <ScreenTransitionWrapper screenName={screen.name}>
        <div className="flex h-[60px] shrink-0 items-center justify-between gap-6 border-0 border-b border-solid border-grey-100 p-6">
          <div className="flex items-center gap-3">
            <ShieldIcon />
            <span className="font-secondary text-sm font-semibold text-[#383838]">
              {t({
                id: "client-support-page.two-factor-authentication",
                defaultMessage: "Two-factor authentication",
              })}
            </span>
          </div>
          <PlainButton
            className="flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-grey-100"
            onClick={onRequestClose}
          >
            <CloseIcon />
          </PlainButton>
        </div>
        {content}
      </ScreenTransitionWrapper>
    </Dialog>
  );
};

export const WhatsappPinDialog: React.FC<WhatsappPinDialogProps> = ({
  initialScreen,
  ...props
}) => {
  return (
    <ScreenTransitionManagerProvider initialScreen={{ name: initialScreen }}>
      <InnerWhatsappPinDialog initialScreen={initialScreen} {...props} />
    </ScreenTransitionManagerProvider>
  );
};
